<!-- src/components/forms/completed/PpeRequestCompleted.vue -->
<template>
  <div class="space-y-12 animate-fadeIn">
    
    <!-- 1. Identity Snapshot -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <IdentificationIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Personnel Details</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="StatCard bg-white">
          <span class="label">First Name</span>
          <span class="value uppercase">{{ submissionData.firstName }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Last Name</span>
          <span class="value uppercase">{{ submissionData.lastName }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Verified Email</span>
          <span class="value lowercase text-xs">{{ submissionData.email }}</span>
        </div>
      </div>
    </section>

    <!-- 2. Equipment Requisition -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <WrenchScrewdriverIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Equipment Requisition</h3>
      </div>

      <!-- Items Grid -->
      <div v-if="hasItems" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div v-for="item in submissionData.items" :key="item.name" 
             class="flex flex-col p-6 bg-white border border-slate-200 rounded-[28px] shadow-sm relative overflow-hidden group hover:border-[#20FCF1] transition-all">
          <div class="absolute top-0 right-0 p-4">
            <div class="bg-[#EBFDF8] p-2 rounded-full">
              <InboxStackIcon class="h-4 w-4 text-[#20FCF1]" />
            </div>
          </div>
          <span class="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">Requested Item</span>
          <p class="text-sm font-black text-[#0A1630] uppercase tracking-tight mb-4">{{ item.name }}</p>
          <div class="flex gap-3">
            <div class="bg-slate-50 px-3 py-1 rounded-lg border border-slate-100">
              <span class="text-[9px] font-black uppercase text-slate-400 mr-2">Size</span>
              <span class="text-xs font-black text-[#0A1630]">{{ item.size || 'N/A' }}</span>
            </div>
            <div class="bg-slate-50 px-3 py-1 rounded-lg border border-slate-100">
              <span class="text-[9px] font-black uppercase text-slate-400 mr-2">Qty</span>
              <span class="text-xs font-black text-[#0A1630]">{{ item.qty }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="p-12 bg-slate-50 rounded-[40px] border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-center">
        <div class="bg-white p-4 rounded-full shadow-sm mb-4">
          <CheckCircleIcon class="h-8 w-8 text-slate-300" />
        </div>
        <p class="text-xs font-black uppercase tracking-widest text-slate-400">No specific PPE items requested in this submission.</p>
      </div>
    </section>

    <!-- 3. Final Attestation -->
    <section class="border-t pt-12">
      <div class="flex flex-col md:flex-row justify-between items-end gap-8">
        <div class="space-y-4">
          <div class="flex items-center gap-2">
            <ShieldCheckIcon class="h-5 w-5 text-[#20FCF1]" />
            <p class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Requisition Record Verified</p>
          </div>
          <div class="p-6 bg-slate-900 rounded-[32px] border border-slate-800 max-w-sm">
            <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Employee Declaration</p>
            <p v-if="submissionData.confirmationReceived" class="text-xs text-slate-300 font-medium italic leading-relaxed">
              "I confirm I have received and am responsible for the care of the Personal Protective Equipment listed above."
            </p>
            <p v-else class="text-xs text-slate-300 font-medium italic leading-relaxed">
              "I confirm I have not received any Personal Protective Equipment."
            </p>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl || submissionData.signatureUrl" class="bg-white p-6 rounded-[32px] border border-slate-200 shadow-xl flex flex-col items-center min-w-[260px]">
          <p class="text-[9px] font-black uppercase text-slate-400 mb-4 tracking-widest">Digital Attestation Seal</p>
          <img :src="submissionData.signatureDataUrl || submissionData.signatureUrl" class="max-h-20 object-contain mb-4" />
          <div class="w-full h-px bg-slate-100 mb-4"></div>
          <div class="flex items-center gap-2">
            <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">{{ formatDate(submissionData.submittedAt, 'dateOnly') }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { 
  IdentificationIcon, WrenchScrewdriverIcon, InboxStackIcon, 
  CheckCircleIcon, ShieldCheckIcon, CheckBadgeIcon 
} from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true }
});

const hasItems = computed(() => {
  return props.submissionData.items && Object.keys(props.submissionData.items).length > 0;
});
</script>

<style scoped>
.StatCard { @apply flex flex-col p-6 rounded-[28px] border border-slate-100 shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>